import React, { useState, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  startOfYear,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addYears,
  subYears,
} from "date-fns";
import moment from "moment";
import "./NewCalender.scss";
import { formatDateInTimeZoneUTC, FormatUSD } from "../../../utilities/string";

import { ReactComponent as LeftIcon } from "../../../assets/icons/NewIcons/LeftIcon.svg";
import { ReactComponent as RightIcon } from "../../../assets/icons/NewIcons/RightIcon.svg";

const NewCalender = ({
  data,
  setSelectedDateValue,
  selectedDateValue,
  setDateWiseData,
  setSelectedDate,
  selectedDate,
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [events, setEvents] = useState({});

  const [month, setMonth] = useState("month");

  const toUTCDate = (date) => {
    return moment.utc(date).startOf("day").toDate();
  };

  // const formatUTCDate = (date) => {
  //   return moment.utc(date).format("DD-MM-YYYY");
  // };

  useEffect(() => {
    if (data) {
      const tradingProfits = {};

      data?.forEach((trade) => {
        // Format the trade date to a consistent format (like DD-MM-YYYY)
        const tradeDate = formatDateInTimeZoneUTC(trade?.date, "DD-MM-YYYY");

        // Initialize the trade array for that day if not present
        if (!tradingProfits[tradeDate]) {
          tradingProfits[tradeDate] = {
            trades: [],
            totalProfit: 0,
            tradeCount: 0,
          };
        }

        // Push the trade to the trades array for that day
        tradingProfits[tradeDate].trades.push(trade);

        // Calculate total profit including both profit and swap
        const totalTradeProfit = (trade?.profit || 0) + (trade?.swap || 0);
        
        // Accumulate the profit and increment the trade count for the day
        tradingProfits[tradeDate].totalProfit += totalTradeProfit;
        tradingProfits[tradeDate].tradeCount += 1;
      });

      setEvents(tradingProfits);
      setDateWiseData(tradingProfits);
    }
  }, [JSON.stringify(data)]);

  const renderYearView = () => {
    const yearStart = startOfYear(currentMonth);
    const months = [];

    for (let i = 0; i < 12; i++) {
      const monthStart = addMonths(yearStart, i);
      const formattedMonth = format(monthStart, "MMMM");
      const formattedDate = format(monthStart, "yyyy-MM-dd");
      const monthName = moment(formattedDate, "YYYY-MM-DD").format("MMMM");

      let totalProfitOrLoss = 0;
      let result = {
        totalProfitOrLoss: 0,
        monthName: null,
      };

      const checkYear = moment(formattedDate).year();
      const checkMonth = moment(formattedDate).month();

      Object.keys(events)?.forEach((key) => {
        const [day, month, year] = key.split("-");
        const keyYear = parseInt(year, 10);
        const keyMonth = parseInt(month, 10) - 1; // Convert to 0-based month index

        // Check if month and year match
        if (keyYear === checkYear && keyMonth === checkMonth) {
          result.monthName = moment(formattedDate).format("MMMM");
          result.totalProfitOrLoss += events[key].totalProfit;
        }
      });

      // Determine border color based on total profit/loss
      const borderColor = result.totalProfitOrLoss < 0 
        ? "#cb2f2e"  // Red for loss
        : result.totalProfitOrLoss > 0 
        ? "rgb(100, 193, 68)"  // Green for profit
        : "transparent";  // No border for zero

      months.push(
        <div
          key={i}
          className={`month-cell cell col ${
            result.totalProfitOrLoss !== 0 ? "month-with-events" : ""
          }`}
          onClick={() => {
            setCurrentMonth(monthStart);
            setMonth("month");
          }}
          style={{
            border: `1px solid ${borderColor}`,
          }}
        >
          <span>{formattedMonth}</span>
          {result.totalProfitOrLoss !== 0 && (
            <div className="month-events">
              {result.totalProfitOrLoss > 0
                ? `Profit: ${FormatUSD(result.totalProfitOrLoss)}`
                : `Loss: ${FormatUSD(Math.abs(result.totalProfitOrLoss))}`}
            </div>
          )}
        </div>
      );
    }
    return <div className="year-view">{months}</div>;
  };

  const renderHeader = () => (
    <div className="calendar-header">
      <div className="calendar-header_left">
        <button onClick={month === "month" ? prevMonth : prevYear}>
          <LeftIcon />
        </button>

        <span>
          {month === "month"
            ? format(currentMonth, "MMMM yyyy") // Show full month and year
            : format(currentMonth, "yyyy")}{" "}
          {/* Show only the year */}
        </span>

        <button onClick={month === "month" ? nextMonth : nextYear}>
          <RightIcon />
        </button>
      </div>

      <div className="calendar-header_right">
        <button
          className={`${month === "month" ? "active" : ""}`}
          onClick={() => setMonth("month")}
        >
          Month
        </button>
        <button
          className={`${month === "year" ? "active" : ""}`}
          onClick={() => {
            setMonth("year");
            setSelectedDateValue(false);
          }}
        >
          Year
        </button>
      </div>
    </div>
  );

  // 3. Handle the click on a month to go back to the selected month's view
  const onMonthClick = (monthStart) => {
    setCurrentMonth(monthStart);
    setMonth("month");
  };

  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(currentMonth);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div key={i} className="day-name">
          {format(addDays(startDate, i), "E")}
        </div>
      );
    }

    return <div className="days-row">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      formattedDate = format(day, "dd-MM-yyyy");
      const cloneDay = day;

      const eventForDay = events[formattedDate];

      // Calculate total profit or loss for the day
      const totalProfitOrLoss = eventForDay ? eventForDay.totalProfit : 0;

      days.push(
        <div
          className={`col cell ${
            !isSameMonth(day, monthStart)
              ? "disabled"
              : isSameDay(day, selectedDate)
              ? "selected"
              : ""
          } ${
            eventForDay &&
            (eventForDay?.totalProfit > 0
              ? "profit-event"
              : eventForDay?.totalProfit < 0
              ? "loss-event"
              : "normal-event")
          }`}
          key={day}
          onClick={() => onDateClick(cloneDay)}
        >
          <span
            className={`number ${
              eventForDay?.totalProfit > 0
                ? "profit_day"
                : eventForDay?.totalProfit < 0
                ? "loss_day"
                : "normal_day"
            }`}
          >
            {format(day, "d")}
          </span>
          {eventForDay && (
            <div className="event-title">
              <span
                className={`${
                  totalProfitOrLoss > 0
                    ? "profit_val"
                    : totalProfitOrLoss === 0
                    ? "normal_val"
                    : "loss_val"
                }`}
              >
                {FormatUSD(totalProfitOrLoss)}
              </span>
            </div>
          )}
        </div>
      );
      day = addDays(day, 1);
    }

    rows.push(
      <div className="row" key={day}>
        {days}
      </div>
    );
    days = [];

    return <div className="body">{rows}</div>;
  };

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const nextYear = () => {
    setCurrentMonth(addYears(currentMonth, 1));
  };

  const prevYear = () => {
    setCurrentMonth(subYears(currentMonth, 1));
  };

  const onDateClick = (day) => {
    setSelectedDate(day);
    const utcDate = moment(day).format("DD-MM-YYYY");
    const tradeValues = data?.filter(
      (item) => formatDateInTimeZoneUTC(item?.date) === utcDate
    );

    setSelectedDateValue(tradeValues);
  };

  const calculateTotalProfitLoss = (trades) => {
    if (!trades || trades.length === 0) return 0;
    
    return trades.reduce((total, trade) => {
      const profit = trade?.profit || 0;
      const swap = trade?.swap || 0;
      return total + (profit + swap);
    }, 0);
  };

  return (
    <div className="calendar">
      {renderHeader()}
      <div
        className={`main_calendar_container ${
          selectedDateValue ? "grid" : "auto"
        }`}
      >
        <div className="main_calendar_container_inner">
          {month === "year" ? renderYearView() : renderDays()}
          {month === "month" && renderCells()}
        </div>

        <div>
          {selectedDateValue && (
            <div className="selected_trade_con">
              <div className="selected_trade_con_header">
                <h4>
                  {selectedDateValue[0]?.date ? (
                    formatDateInTimeZoneUTC(
                      selectedDateValue[0]?.date,
                      "MMMM DD, YYYY"
                    )
                  ) : (
                    <span>No transaction on this date</span>
                  )}
                </h4>
                <div
                  onClick={() => setSelectedDateValue(false)}
                  style={{ cursor: "pointer" }}
                >
                  X
                </div>
              </div>

              <span style={{ textWrap: "nowrap" }} className="trade_date">
                {selectedDateValue[0]?.date ? "Trade Summary ," : ""}{" "}
                {selectedDateValue[0]?.date
                  ? formatDateInTimeZoneUTC(
                      selectedDateValue[0]?.date,
                      "MMMM DD, YYYY"
                    )
                  : ""}
              </span>

              <div className="profit_loss_info">
                {selectedDateValue?.map((trade, index) => {
                  const totalTradeProfit = (trade?.profit || 0) + (trade?.swap || 0);
                  
                  return (
                    totalTradeProfit !== 0 && (
                      <div key={index} className="profit_loss_info_inner">
                        <div
                          className={
                            totalTradeProfit < 0
                              ? `loss_dot`
                              : totalTradeProfit === 0
                              ? `normal`
                              : `profit_dot`
                          }
                        ></div>
                        <p>
                          {totalTradeProfit < 0
                            ? `Loss of ${FormatUSD(Math.abs(totalTradeProfit))}`
                            : totalTradeProfit === 0
                            ? `No profit/loss - ${FormatUSD(0)}`
                            : `Profit of ${FormatUSD(totalTradeProfit)}`}
                        </p>
                      </div>
                    )
                  );
                })}
              </div>
              <div className="profit_loss_info_summary">
                <span>Overall Result</span>
                <p>
                  {calculateTotalProfitLoss(selectedDateValue) < 0
                    ? `Loss of `
                    : `Profit of `}
                  <span
                    style={{
                      color:
                        calculateTotalProfitLoss(selectedDateValue) < 0
                          ? "red"
                          : "green", // Red for loss, green for profit
                    }}
                  >
                    {FormatUSD(calculateTotalProfitLoss(selectedDateValue))}
                  </span>
                  {` during trade of ${format(selectedDate, "dd MMMM yyyy")}`}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewCalender;
